import * as v2 from '../v2/constants';

import type {
  TDraftAssessmentSection,
  TPersistedAssessmentItem,
  TDraftAssessmentDependencies,
} from '../types';

type TAssessmentQAItem = TPersistedAssessmentItem['assessmentQA'][number];
type TSectionId = `${v2.SECTION}`;
type TSectionItems = Partial<typeof v2.SECTION_ITEMS>;
type TSectionGroups = Partial<typeof v2.GROUP_BY_SECTION>;

class DraftAssessment {
  sectionGroups: TSectionGroups;
  sectionItems: TSectionItems;

  constructor({
    sectionGroups,
    sectionItems,
  }: {
    sectionGroups: TSectionGroups;
    sectionItems: TSectionItems;
  }) {
    this.sectionGroups = sectionGroups;
    this.sectionItems = sectionItems;
  }

  createSection(sectionId: TSectionId): TDraftAssessmentSection {
    const section: TDraftAssessmentSection = {
      id: sectionId,
      items: {},
    };

    const items = this.sectionItems[sectionId];

    items?.forEach((item) => {
      section.items[item.question] = {
        persist: true,
        id: item.question,
        sectionId: sectionId,
        value: item.input === v2.INPUT.CHIP_MULTIPLE ? [] : '',
        options: {
          type: item.input,
          items: item.answers,
        },
      };

      if (item.visibleIf) {
        section.items[item.question].visibleIf = {
          sectionId: item.visibleIf.section,
          itemId: item.visibleIf.question,
          value: item.visibleIf.answer,
        };
      }

      if (item.comment) {
        section.items[item.comment.id] = {
          persist: false,
          id: item.comment.id,
          sectionId: sectionId,
          value: '',
          options: {
            type: v2.INPUT.TEXT,
          },
          visibleIf: {
            sectionId: item.comment.visibleIf.section,
            itemId: item.comment.visibleIf.question,
            value: item.comment.visibleIf.answer,
          },
        };
      }
    });

    return section;
  }

  createDependencies(sectionId: TSectionId): TDraftAssessmentDependencies {
    const dependencies: TDraftAssessmentDependencies = {};

    const items = this.sectionItems[sectionId];

    items.forEach((item) => {
      dependencies[item.question] = dependencies[item.question] ?? [];

      if (item.visibleIf) {
        const dependencyId = item.visibleIf.question;

        dependencies[dependencyId] = dependencies[dependencyId] ?? [];

        dependencies[dependencyId].push({
          sectionId: item.visibleIf.section,
          itemId: item.question,
        });
      }

      if (item.comment?.visibleIf) {
        const itemId = item.comment.id;

        dependencies[itemId] = dependencies[itemId] ?? [];

        const dependencyId = item.comment.visibleIf.question;

        dependencies[dependencyId] = dependencies[dependencyId] ?? [];

        dependencies[dependencyId].push({
          sectionId: sectionId,
          itemId: itemId,
        });
      }
    });

    return dependencies;
  }

  toPersistedAssessmentItem(
    section: TDraftAssessmentSection,
  ): TPersistedAssessmentItem {
    const persistedItem: TPersistedAssessmentItem = {
      itemGroup: this.sectionGroups[section.id],
      itemType: section.id,
      present: true,
      assessmentQA: [],
    };

    Object.values(section.items).forEach((sectionItem) => {
      if (!sectionItem.persist) {
        return;
      }

      const qaItem: TAssessmentQAItem = {
        question: sectionItem.id as v2.QUESTION,
        answer: sectionItem.value as v2.ANSWER,
      };

      const items = this.sectionItems[section.id as TSectionId];
      const item = items?.find((i) => i.question === sectionItem.id);

      if (item?.comment) {
        const commentId = item.comment.id;
        const commentValue = section.items[commentId]?.value;

        qaItem.comment = Array.isArray(commentValue)
          ? commentValue.join(',')
          : commentValue;
      }

      persistedItem.assessmentQA.push(qaItem);
    });

    return persistedItem;
  }
}

export default DraftAssessment;
